import { Box, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit, MdRestoreFromTrash } from 'react-icons/md'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { UserType } from './constants'

interface SystemAdminProps {
  userType: UserType.SystemAdmin
  data: any
  onEdit: (id: EntityId) => void
  onDelete: (id: EntityId) => void
  onPush: (id: EntityId) => void
  onOptionalize: (id: EntityId) => void
  showActions?: boolean
  showKebabActions?: boolean
}
interface CustomerAdminProps {
  userType: UserType.CustomerAdmin
  data: any
  onEdit: (id: EntityId) => void
  onDelete: (id: EntityId) => void
  onApprove: (id: EntityId) => void
  onReject: (id: EntityId) => void
  onMoveAndDelete: (id: EntityId) => void
  customerId: EntityId
  showActions?: boolean
  showKebabActions?: boolean
}

export const DocumentCategoryTable: React.FC<SystemAdminProps | CustomerAdminProps> = props => {
  const { userType, data, showActions = true, showKebabActions = true } = props
  const { t: translate } = useTranslation(['document-category', 'common', 'global-documents'])
  const theme = useTheme()

  const handleUpdateEdit = useCallback(
    (id: EntityId) => {
      if (userType === UserType.SystemAdmin) (props as SystemAdminProps).onEdit(id)
      else if (userType === UserType.CustomerAdmin) (props as CustomerAdminProps).onEdit(id)
    },
    [userType, props],
  )

  const handleDelete = useCallback(
    (id: EntityId) => {
      if (userType === UserType.SystemAdmin) (props as SystemAdminProps).onDelete(id)
      else if (userType === UserType.CustomerAdmin) (props as CustomerAdminProps).onDelete(id)
    },
    [userType, props],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'categoryName',
        flex: 1,
        cellClassName: 'font-weight-semi-bold',
        renderCell: params => {
          if (userType === UserType.CustomerAdmin) {
            return (
              <Typography component="span" variant="body2">
                {params.value}{' '}
                {params.row.isNew && <NewBadge>{translate('new', { ns: 'document-category' })}</NewBadge>}
              </Typography>
            )
          }
          return params.value
        },
      },
      {
        field: 'options',
        minWidth: 240,
        renderCell: params => {
          if (userType === UserType.SystemAdmin) {
            return (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                {params && params.row.isForcePushed ? (
                  <GridActionButton
                    onClick={() => (props as SystemAdminProps).onOptionalize(params.row.id)}
                    buttonText={translate('undo-force-and-push', { ns: 'document-category' })}
                    bgColor={getThemeColor(theme, MUIThemeColors.secondaryMain)}
                    textColor={`${getThemeColor(theme, MUIThemeColors.white)} !important`}
                    height={'32px'}
                    buttonTextProps={{ paddingLeft: '24px', paddingRight: '24px', fontWeight: 400 }}
                  />
                ) : (
                  <GridActionButton
                    onClick={() => (props as SystemAdminProps).onPush(params.row.id)}
                    buttonText={translate('force-and-push', { ns: 'document-category' })}
                    bgColor={getThemeColor(theme, MUIThemeColors.primaryMain)}
                    textColor={`${getThemeColor(theme, MUIThemeColors.white)} !important`}
                    height={'32px'}
                    buttonTextProps={{ paddingLeft: '24px', paddingRight: '24px', fontWeight: 400 }}
                  />
                )}
              </Box>
            )
          }
          if (userType === UserType.CustomerAdmin) {
            return (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                {!showActions ? (
                  <></>
                ) : params && params.row.customerId ? (
                  <></>
                ) : params.row.isForcePushed ? (
                  <></>
                ) : params.row.isEnabled ? (
                  <GridActionButton
                    onClick={() => (props as CustomerAdminProps).onReject(params.id)}
                    buttonText={translate('reject', { ns: 'document-category' })}
                    bgColor={getThemeColor(theme, MUIThemeColors.secondaryMain)}
                    textColor={`${getThemeColor(theme, MUIThemeColors.white)} !important`}
                    height={'32px'}
                    buttonTextProps={{ paddingLeft: '24px', paddingRight: '24px', fontWeight: 400 }}
                  />
                ) : (
                  <GridActionButton
                    onClick={() => (props as CustomerAdminProps).onApprove(params.row.id)}
                    buttonText={translate('approve', { ns: 'document-category' })}
                    disabled={params.row.isForcePushed}
                    bgColor={getThemeColor(theme, MUIThemeColors.primaryMain)}
                    textColor={`${getThemeColor(theme, MUIThemeColors.white)} !important`}
                    height={'32px'}
                    buttonTextProps={{ paddingLeft: '24px', paddingRight: '24px', fontWeight: 400 }}
                  />
                )}
              </Box>
            )
          }
          return null
        },
      },
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as GridNativeColTypes,
        minWidth: 10,
        getActions: ({ id, row }) => {
          if (
            (userType == UserType.CustomerAdmin && row.isForcePushed) ||
            (userType == UserType.CustomerAdmin && row.isEnabled)
          ) {
            return []
          }

          return showKebabActions
            ? [
                <GridActionsCellItem
                  key={`edit-${id}`}
                  label={translate('edit', { ns: 'common' })}
                  icon={<MdEdit size={24} />}
                  onClick={() => handleUpdateEdit(id)}
                  color="primary"
                  showInMenu
                />,
                <GridActionsCellItem
                  key={`delete-${id}`}
                  label={translate('delete', { ns: 'common' })}
                  icon={<MdDelete size={24} />}
                  onClick={() => handleDelete(id)}
                  color="primary"
                  showInMenu
                />,
                userType === UserType.CustomerAdmin ? (
                  <GridActionsCellItem
                    key={`move-${id}`}
                    label={translate('move-and-delete', { ns: 'document-category' })}
                    icon={<MdRestoreFromTrash size={24} />}
                    onClick={() => (props as CustomerAdminProps).onMoveAndDelete(id)}
                    color="primary"
                    showInMenu
                  />
                ) : (
                  <></>
                ),
              ]
            : []
        },
      },
    ],
    [userType, translate, theme, props, handleUpdateEdit, handleDelete],
  )
  return (
    <GenericTable
      id={'customer-document-categories'}
      data={data}
      columns={columns}
      defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
      enablePagination={false}
      hideDefaultFooterPagination={true}
      getRowClassName={() => ''}
      autoHeight={false}
      height={userType === UserType.SystemAdmin ? window.innerHeight - 230 + 'px' : window.innerHeight - 280 + 'px'}
      hideColumnHeaders={true}
      rowBottomSpacing={4}
    />
  )
}

export const NewBadge = styled('span')(({ theme }) => ({
  fontSize: '10px',
  color: getThemeColor(theme, MUIThemeColors.white),
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  border: '1px solid #D9E1E7CC',
  borderRadius: '2px',
  opacity: '1',
  padding: '1px 3px',
  marginBottom: '15px',
}))
